import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord } from 'services/UserService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
injectReducer('user', reducer)

export default function User() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const user = useSelector(state => state.auth.user)
    const { data, tableData, loading } = useSelector(state => state.user.data)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.company,
        tableData.registration_status,
    ])

    const fetchData = () => {
        const params = user?.company ? { ...tableData, company: user?.company?.id } : { ...tableData }
        if (tableData.registration_status) {
            params.registration_status = tableData.registration_status
        }
        dispatch(getData(params))
    }

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                // renderCell: row => row?.details?.companies[0]?.name,
            },
            {
                field: 'name',
                header: 'Name',
                className: 'min-w-[150px]',
                renderCell: row => {
                    const firstName = row?.user?.first_name || ''
                    const lastName = row?.user?.last_name || ''
                    return `${firstName} ${lastName}`.trim() || 'N/A'
                }
            },
            {
                field: 'registration_status',
                header: 'Status',
                className: 'min-w-[120px]',
                renderCell: row => {
                    const status = row?.user?.registration_status
                    const statusColors = {
                        'complete': 'bg-green-100 text-green-800 border-green-200',
                        'incomplete': 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }
                    const statusIcons = {
                        'complete': '✓',
                        'incomplete': '⚠️'
                    }
                    
                    return (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                            <span className="mr-1">{statusIcons[status] || '?'}</span>
                            {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                        </span>
                    )
                }
            },
            {
                field: 'email',
                header: 'Email',
                className: 'min-w-[200px]',
                renderCell: row => {
                    const email = row?.user?.email
                    if (!email) {
                        return <span className="text-gray-500 italic">Not provided</span>
                    }
                    return (
                        <div className="flex items-center">
                            <span>📧</span>
                            <span className="ml-2">{email}</span>
                        </div>
                    )
                }
            },
            {
                field: 'phone_number',
                header: 'Phone Number',
                className: 'min-w-[150px]',
                renderCell: row => {
                    const phone = row?.user?.phone_number
                    const mobile = row?.user?.mobile // Keep backward compatibility
                    const displayPhone = phone || mobile
                    
                    if (!displayPhone) {
                        return <span className="text-gray-500 italic">Not provided</span>
                    }
                    return (
                        <div className="flex items-center">
                            <span>📱</span>
                            <span className="ml-2">{displayPhone}</span>
                        </div>
                    )
                }
            },
            {
                field: 'contact_methods',
                header: 'Contact Info',
                className: 'min-w-[100px]',
                renderCell: row => {
                    const email = row?.user?.email
                    const phone = row?.user?.phone_number || row?.user?.mobile
                    const methods = []
                    
                    if (email) methods.push('📧')
                    if (phone) methods.push('📱')
                    
                    return (
                        <div className="flex items-center space-x-1">
                            {methods.length > 0 ? methods.join(' ') : <span className="text-gray-500">No contact</span>}
                        </div>
                    )
                }
            },
            {
                header: 'Action',
                className: 'px-4',
                renderCell: row => {
                    const isIncomplete = row?.user?.registration_status === 'incomplete'
                    
                    return (
                        <div className="flex items-center gap-3">
                            {/* Complete Registration Button for Incomplete Users */}
                            {isIncomplete && checkPermission('user', 'edit') && (
                                <Tooltip title={'Complete Registration'}>
                                    <Link 
                                        to={`/user/${row?.user?.id}/complete`} 
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 border border-green-200 rounded-full hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/40"
                                    >
                                        <span className="mr-1">✓</span>
                                        Complete
                                    </Link>
                                </Tooltip>
                            )}
                            
                            {/* Edit Button */}
                            {checkPermission('user', 'edit') && (user.super_user || user?.id === row?.user?.id) ? (
                                <Tooltip title={'Edit'}>
                                    <Link to={`/user/${row?.user?.id}/edit`} className="hover:text-primary">
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            
                            {/* Delete Button */}
                            {checkPermission('user', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={'Are you sure you want to delete this record!'}
                                        onConfirm={() => {
                                            handleDelete(row?.user?.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [tableData.sort, tableData.query, tableData.pageIndex, tableData.pageSize, tableData.selectedTab, tableData.company, tableData.registration_status]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Users</h2>
                <div className="flex items-center gap-2">
                    {/* Registration Status Filter */}
                    <div className="flex bg-gray-100 rounded-lg p-1 dark:bg-gray-800">
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: null, pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                !tableData.registration_status 
                                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            All Users
                        </button>
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: 'incomplete', pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                tableData.registration_status === 'incomplete' 
                                    ? 'bg-yellow-100 text-yellow-800 shadow-sm dark:bg-yellow-900 dark:text-yellow-100' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            <span className="mr-1">⚠️</span>
                            Incomplete ({data?.results?.filter(item => item?.user?.registration_status === 'incomplete').length || 0})
                        </button>
                        <button
                            onClick={() => dispatch(setTableData({ ...tableData, registration_status: 'complete', pageIndex: 1 }))}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                tableData.registration_status === 'complete' 
                                    ? 'bg-green-100 text-green-800 shadow-sm dark:bg-green-900 dark:text-green-100' 
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            <span className="mr-1">✓</span>
                            Complete
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Incomplete Registration Notice */}
            {tableData.registration_status === 'incomplete' && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
                    <div className="flex items-start">
                        <span className="text-yellow-600 mr-2">⚠️</span>
                        <div>
                            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                Incomplete Customer Registrations
                            </h3>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                These customers were registered without email addresses. You can complete their registration by adding an email address.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                <Table
                    loading={loading}
                    filter={false}
                    companyFilter={user.super_user}
                    datas={data}
                    columns={columns}
                    add_new={checkPermission('user', 'create') ? '/user/create' : ''}
                    pagingData={tableData}
                    onChange={e => dispatch(setTableData(e))}
                />
            </div>
        </>
    )
}
